import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { useAuth } from "../context/AuthContext";

const getDefaultRoute = (user) => {
  if (user?.role === "partner" && user?.partnerUniqueId) {
    return `/partner/${user.partnerUniqueId}`;
  }

  return "/admin/partners";
};

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, login, refreshUser } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;
  const redirectTarget = location.state?.from?.pathname;
  const safeRedirectTarget =
    redirectTarget && redirectTarget !== "/login" && redirectTarget !== "/logout"
      ? redirectTarget
      : null;

  if (isAuthenticated) {
    return <Navigate to={safeRedirectTarget || getDefaultRoute(user)} replace />;
  }

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      if (response.ok) {
        login(data);
        const activeUser = await refreshUser();
        if (!activeUser) {
          throw new Error("Сессия не сохранилась. Проверьте cookie и CORS.");
        }

        toastr.success("Вы успешно вошли в систему!");

        navigate(safeRedirectTarget || getDefaultRoute(activeUser), { replace: true });
      } else {
        setError(data.message || "Ошибка авторизации");
        toastr.error(data.message || "Ошибка авторизации");
      }
    } catch (err) {
      const message = err.message || "Ошибка соединения с сервером";
      setError(message);
      toastr.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    handleLogin();
  };

  return (
    <section className="auth-shell">
      <div className="auth-wrap">
        <div className="auth-side">
          <div className="auth-hero-logo">
            <img src="/logo.png" alt="Partner Panel" />
          </div>
        </div>

        <div className="auth-card shadow-lg">
          <div className="auth-card__header">
            <p className="eyebrow text-muted mb-1">Доступ к панели</p>
            <h2>Вход в аккаунт</h2>
            <p className="text-muted">Введите корпоративный логин и пароль, чтобы продолжить.</p>
          </div>

          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

          {loading ? (
            <div className="auth-loader text-center">
              <Spinner animation="border" variant="secondary" />
              <p className="mt-3 text-muted">Проверяем данные…</p>
            </div>
          ) : (
            <Form onSubmit={handleSubmit} className="auth-form">
              <Form.Group className="mb-3" controlId="formUsername">
                <Form.Label>Имя пользователя</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Например: partner.manager"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                />
              </Form.Group>

              <Form.Group className="mb-4" controlId="formPassword">
                <Form.Label>Пароль</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Введите пароль"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100 btn-rounded">
                Войти в систему
              </Button>
            </Form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Login;
